import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
} from "react";
import { jwtDecode } from "jwt-decode";

import {
  loginUser,
  refreshToken,
  logoutUser,
  getAccessToken,
  isTokenExpired,
} from "../services/AuthService";

interface AuthContextProps {
  user: string | null;
  token: string | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<boolean>;
  logout: () => void;
}

export const AuthContext = createContext<AuthContextProps | undefined>(
  undefined,
);

export const AuthContextProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [user, setUser] = useState<string | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(true); // <-- track if we are still checking the token

  const login = async (email: string, password: string): Promise<boolean> => {
    try {
      const response = await loginUser(email, password);
      if (response) {
        // Check if user hasn't logged out during the login process
        const currentToken = getAccessToken();
        if (!currentToken) {
          // User logged out during login process, don't set state
          return false;
        }

        setToken(response.access_token);

        const decoded: any = jwtDecode(response.access_token);
        setUser(decoded.email || null);

        return true;
      }
    } catch (error) {
      console.error("Login failed:", error);
    }
    return false;
  };

  const logout = useCallback(() => {
    logoutUser();
    setToken(null);
    setUser(null);
  }, []);

  useEffect(() => {
    const initializeAuth = async () => {
      setLoading(true);

      const storedToken = getAccessToken();
      if (storedToken && !isTokenExpired(storedToken)) {
        setToken(storedToken);
        const decoded: any = jwtDecode(storedToken);
        setUser(decoded.email || null);
      } else {
        const newToken = await refreshToken();
        if (newToken) {
          setToken(newToken);
          const decoded: any = jwtDecode(newToken);
          setUser(decoded.email || null);
        } else {
          logout();
        }
      }

      setLoading(false);
    };

    initializeAuth();
  }, [logout]);

  useEffect(() => {
    const interval = setInterval(
      async () => {
        if (token && !isTokenExpired(token)) {
          const newToken = await refreshToken();
          if (newToken) {
            setToken(newToken);
            const decoded: any = jwtDecode(newToken);
            setUser(decoded.email || null);
          } else {
            logout();
          }
        }
      },
      4 * 60 * 1000,
    );

    return () => clearInterval(interval);
  }, [token, logout]);

  return (
    <AuthContext.Provider value={{ user, token, loading, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuthContext = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuthContext should be used within AuthContextProvider");
  }
  return context;
};
